/**
 * SelectoAdapter -- QR ordering integration via iframe bridge.
 *
 * Loads the SelectO customer frontend in a sandboxed iframe and
 * bridges auth, theme, locale, and order events via postMessage.
 */

import type { AweniContext, AweniModule, IframeMessage } from '../../types';

export interface VenueInfo {
  id: number;
  name: string;
  slug: string;
  logo?: string;
  rating?: number;
}

const OUTBOUND = {
  AUTH_STATE: 'AWENI_AUTH_STATE',
  THEME: 'AWENI_THEME',
  LOCALE: 'AWENI_LOCALE',
} as const;

const INBOUND = {
  ORDER_PLACED: 'AWENI_ORDER_PLACED',
  ORDER_UPDATED: 'AWENI_ORDER_UPDATED',
  NAVIGATE: 'AWENI_NAVIGATE',
  READY: 'AWENI_READY',
} as const;

export default class SelectoAdapter implements AweniModule {
  private container: HTMLElement | null = null;
  private context: AweniContext | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private messageHandler: ((event: MessageEvent) => void) | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.context = ctx;
    const t = ctx.i18n.t.bind(ctx.i18n);

    container.innerHTML = `
      <div class="selecto-adapter relative w-full h-full" style="min-height: calc(100vh - 120px)">
        <div class="selecto-skeleton absolute inset-0 flex flex-col items-center justify-center gap-4" style="background: var(--bg-secondary)">
          <div class="w-16 h-16 rounded-2xl animate-pulse" style="background: #F9731640"></div>
          <div class="flex flex-col items-center gap-2">
            <div class="h-4 w-32 rounded animate-pulse" style="background: var(--border)"></div>
            <p class="text-sm" style="color: var(--text-secondary)">${t('selecto_loading')}</p>
          </div>
        </div>
        <iframe
          id="selecto-iframe"
          class="w-full h-full border-0 opacity-0 transition-opacity duration-300"
          style="min-height: calc(100vh - 120px)"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          loading="lazy"
          title="${t('selecto_title')}"
          aria-label="${t('selecto_title')}"
        ></iframe>
      </div>
    `;

    this.iframe = container.querySelector<HTMLIFrameElement>('#selecto-iframe');
    if (!this.iframe) return;

    this.messageHandler = (e: MessageEvent) => this.handleMessage(e);
    window.addEventListener('message', this.messageHandler);

    this.iframe.addEventListener('load', () => {
      const skeleton = container.querySelector('.selecto-skeleton');
      if (skeleton) skeleton.remove();
      if (this.iframe) this.iframe.classList.remove('opacity-0');
      this.sendState();
    });

    this.iframe.addEventListener('error', () => {
      const skeleton = container.querySelector('.selecto-skeleton');
      if (skeleton) {
        skeleton.innerHTML = `
          <div class="text-center p-6">
            <svg class="w-12 h-12 mx-auto mb-3" style="color: var(--text-secondary)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
            </svg>
            <p class="text-sm" style="color: var(--text-secondary)">${t('selecto_error')}</p>
          </div>
        `;
      }
    });

    this.iframe.src = '/Projects/SelectO/frontend/';
  }

  unmount(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    if (this.iframe) {
      this.iframe.src = 'about:blank';
      this.iframe.remove();
      this.iframe = null;
    }
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.container = null;
    this.context = null;
  }

  private sendState(): void {
    if (!this.context) return;
    this.sendToIframe(OUTBOUND.AUTH_STATE, {
      token: this.context.auth.getToken(),
      isAuthenticated: this.context.auth.isAuthenticated(),
    });
    this.sendToIframe(OUTBOUND.THEME, {
      mode: this.context.theme.mode,
      isDark: this.context.theme.isDark,
    });
    this.sendToIframe(OUTBOUND.LOCALE, {
      locale: this.context.i18n.locale,
      dir: this.context.i18n.dir,
    });
  }

  private sendToIframe(type: string, payload: Record<string, unknown>): void {
    if (!this.iframe?.contentWindow) return;
    try {
      this.iframe.contentWindow.postMessage({ type, payload } as IframeMessage, '*');
    } catch {
      // Iframe not ready
    }
  }

  private handleMessage(event: MessageEvent): void {
    if (!event.data || typeof event.data.type !== 'string') return;
    const { type, payload } = event.data as IframeMessage;

    switch (type) {
      case INBOUND.READY:
        this.sendState();
        break;
      case INBOUND.ORDER_PLACED:
        this.onOrderPlaced(payload);
        break;
      case INBOUND.ORDER_UPDATED:
        // Could update notification badge
        break;
      case INBOUND.NAVIGATE:
        if (payload.module && this.context) {
          this.context.navigate(payload.module as string, payload.path as string | undefined);
        }
        break;
    }
  }

  private onOrderPlaced(payload: Record<string, unknown>): void {
    try {
      this.context?.api.post('/aweni/api/v1/activity', {
        app: 'selecto',
        action: 'order_placed',
        label: (payload.venueName as string) || 'Order',
        meta: { orderId: payload.orderId },
      });
    } catch {
      // Non-critical
    }
    if (this.context) {
      this.context.showNotification(
        this.context.i18n.t('selecto_title') + ' - Order placed!',
        'success',
      );
    }
  }
}
